import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

// rainColor = '#4FC3F7',

const GrokIcon = ({ 
  size = 456, 
  animateRain = true, 
  rainIntensity = 0.5,
  rainColor = '#4FC3F7',
  rainSpeed = 1.0
}) => {
  const containerRef = useRef(null);
  const rainRef = useRef({ drops: [], animations: [] });

  useEffect(() => {
    if (!animateRain) {
      // Clean up any existing rain
      cleanupRain();
      return;
    }

    if (!containerRef.current) return;

    // Clear previous rain
    cleanupRain();

    // Create rain group
    const rainGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    rainGroup.setAttribute('class', 'rain-group');
    containerRef.current.appendChild(rainGroup);

    // Define antenna positions
    const antennas = [
      { x: 408, y: 40, width: 16, height: 96 },
      { x: 40, y: 40, width: 16, height: 96 },
      { x: 88, y: 0, width: 16, height: 80 },
      { x: 360, y: 56, width: 16, height: 40 },
      { x: 184, y: 8, width: 16, height: 32 },
      { x: 136, y: 32, width: 16, height: 40 },
      { x: 312, y: 8, width: 16, height: 56 }
    ];

    // Calculate number of drops based on intensity
    const totalDrops = Math.floor(antennas.length * 5 * rainIntensity);
    const dropsPerAntenna = Math.ceil(totalDrops / antennas.length);

    // Create rain drops
    for (let i = 0; i < totalDrops; i++) {
      const antennaIndex = i % antennas.length;
      const antenna = antennas[antennaIndex];
      const dropIndex = Math.floor(i / antennas.length);
      
      const drop = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      
      // Randomize drop properties
      const dropWidth = 1.5 + Math.random() * 5;
      const dropHeight = 5 + Math.random() * 8;
      const dropX = antenna.x + Math.random() * (antenna.width - dropWidth);
      const startY = -30 - dropIndex * 10;
      const endY = antenna.y + antenna.height + 30;
      const baseDuration = 1.0 / rainSpeed;
      const duration = baseDuration * (0.8 + Math.random() * 0.4);
      const initialDelay = (dropIndex * 0.15) + (antennaIndex * 0.05);
      const opacity = 0.2 + Math.random() * 0.4;
      
      drop.setAttribute('x', dropX.toString());
      drop.setAttribute('y', startY.toString());
      drop.setAttribute('width', dropWidth.toString());
      drop.setAttribute('height', dropHeight.toString());
      drop.setAttribute('fill', rainColor);
      drop.setAttribute('rx', '1');
      drop.setAttribute('ry', '1');
      drop.setAttribute('opacity', opacity.toString());
      drop.setAttribute('class', 'rain-drop');
      
      rainGroup.appendChild(drop);
      
      // Store drop reference
      const dropObj = { element: drop, antenna };
      rainRef.current.drops.push(dropObj);
      
      // Create animation for this drop
      const animateDrop = () => {
        // Randomize position each cycle
        const newX = antenna.x + Math.random() * (antenna.width - dropWidth);
        gsap.set(drop, {
          attr: { 
            x: newX,
            y: startY 
          },
          opacity: opacity
        });
        
        // Animate down
        const animation = gsap.to(drop, {
          attr: { y: endY },
          duration: duration,
          ease: 'linear',
          onComplete: () => {
            // Restart animation
            animateDrop();
          }
        });
        
        // Store animation reference
        rainRef.current.animations.push(animation);
      };
      
      // Start animation with delay
      setTimeout(animateDrop, initialDelay * 1000);
    }

    // Store rain group reference
    rainRef.current.group = rainGroup;

    // Cleanup function
    return cleanupRain;
  }, [animateRain, rainIntensity, rainColor, rainSpeed]);

  const cleanupRain = () => {
    // Kill all animations
    rainRef.current.animations.forEach(animation => {
      if (animation && animation.kill) {
        animation.kill();
      }
    });
    
    // Remove rain group
    if (rainRef.current.group && rainRef.current.group.parentNode) {
      rainRef.current.group.parentNode.removeChild(rainRef.current.group);
    }
    
    // Clear references
    rainRef.current = { drops: [], animations: [], group: null };
  };

  return (
    <svg
      ref={containerRef}
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox="-51.2 -51.2 614.40 614.40"
      xmlSpace="preserve"
      width={size}
      height={size}
      fill="#000000"
      style={{ overflow: 'visible' }}
    >
      {/* Original SVG content remains the same */}
      <g>
        <rect x="296" y="328" fill="#67C18C" width="192" height="96" />
        
        <path
          fill="#FFFFFF"
          d="M415.264,263.48c-33.528-20.704-78.192-20.48-111.504,0.64c-1.88-66.784-15.552-133.592-39.824-165.216C344.904,113.144,407.832,180.392,415.264,263.48z"
        />
        <path
          fill="#FFFFFF"
          d="M48.736,263.48c7.432-83.08,70.36-150.328,151.328-164.576c-24.272,31.624-37.944,98.432-39.824,165.216C126.928,242.984,82.264,242.768,48.736,263.48z"
        />
        <path
          fill="#FFFFFF"
          d="M176.272,263.864C179.032,164.176,206.856,96,232,96s52.968,68.176,55.728,167.864c-14.424-9.032-30.912-14.184-47.728-15.416V248h-16v0.448C207.192,249.68,190.696,254.832,176.272,263.864z"
        />

        <path
          fill="#DCDDDE"
          d="M424,280c-17.92-16.24-41.52-24.24-65.04-24C351.12,161.28,297.28,88,232,88C338,88,424,174,424,280z"
        />
        <path
          fill="#DCDDDE"
          d="M232,88c-35.36,0-64,86-64,192c-17.44-15.76-40.08-23.76-62.96-24C112.88,161.28,166.72,88,232,88z"
        />
        <path fill="#DCDDDE" d="M296,280c-17.68-16-40.8-24-64-24V88C267.36,88,296,174,296,280z" />

        <circle fill="#FFD768" cx="136" cy="352" r="40" />
        <circle fill="#FFD768" cx="48" cy="408" r="32" />
        <circle fill="#FFD768" cx="120" cy="472" r="24" />

        <rect x="440" y="368" fill="#FFFFFF" width="16" height="16" />
        <rect x="328" y="368" fill="#FFFFFF" width="16" height="16" />

        {/* light antennas / lines on top */}
        {/* <rect x="408" y="40" fill="#F2F2F2" width="16" height="96" />
        <rect x="40" y="40" fill="#F2F2F2" width="16" height="96" />
        <rect x="88" y="0" fill="#F2F2F2" width="16" height="80" />
        <rect x="360" y="56" fill="#F2F2F2" width="16" height="40" />
        <rect x="184" y="8" fill="#F2F2F2" width="16" height="32" />
        <rect x="136" y="32" fill="#F2F2F2" width="16" height="40" />
        <rect x="312" y="8" fill="#F2F2F2" width="16" height="56" /> */}

        {/* Main outline + details */}
        <path
          fill="#0C3847"
          d="M288,472c0,13.232-10.768,24-24,24s-24-10.768-24-24V264.328c18.4,1.6,36.32,8.752,50.424,21.424l5.016,5.544l0.56-0.512l0.56,0.512l5.016-5.544c32.352-29.08,84.784-29.032,117.056,0.176L432,298.032V280c0-107.6-85.416-195.576-192-199.8V56h-16v24.2C117.416,84.424,32,172.4,32,280v18.032l13.368-12.104c32.264-29.2,84.704-29.256,117.056-0.176l5.016,5.544l0.56-0.512l0.56,0.512l5.016-5.544C187.68,273.08,205.6,265.928,224,264.328V472c0,22.056,17.944,40,40,40s40-17.944,40-40v-8h-16V472z M415.264,263.48c-33.528-20.704-78.192-20.48-111.504,0.64c-1.88-66.784-15.552-133.592-39.824-165.216C344.904,113.144,407.832,180.392,415.264,263.48z M48.736,263.48c7.432-83.08,70.36-150.328,151.328-164.576c-24.272,31.624-37.944,98.432-39.824,165.216C126.928,242.984,82.264,242.768,48.736,263.48z M176.272,263.864C179.032,164.176,206.856,96,232,96s52.968,68.176,55.728,167.864c-14.424-9.032-30.912-14.184-47.728-15.416V248h-16v0.448C207.192,249.68,190.696,254.832,176.272,263.864z"
        />

        <path
          fill="#0C3847"
          d="M392,344c-17.648,0-32,14.352-32,32s14.352,32,32,32s32-14.352,32-32S409.648,344,392,344z M392,392c-8.824,0-16-7.176-16-16s7.176-16,16-16s16,7.176,16,16S400.824,392,392,392z"
        />
        <path
          fill="#0C3847"
          d="M280,312v128h224V312H280z M456.448,421.36l-0.92,2.64H328.52l-1.016-2.76c-4.712-12.832-16.32-24.408-28.864-28.8l-2.64-0.912v-31l2.76-1.016c12.84-4.72,24.408-16.32,28.792-28.864l0.92-2.648h127l1.016,2.76c4.712,12.832,16.32,24.408,28.864,28.8l2.648,0.912v31l-2.76,1.016C472.4,397.208,460.832,408.816,456.448,421.36z M488,343.312c-6.208-3.04-12.072-8.92-15.272-15.312H488V343.312z M311.312,328c-3.04,6.208-8.92,12.072-15.312,15.272V328H311.312z M296,408.688c6.208,3.04,12.072,8.92,15.272,15.312H296V408.688z M472.688,424c3.04-6.208,8.92-12.072,15.312-15.272V424H472.688z"
        />
        <path
          fill="#0C3847"
          d="M136,304c-26.472,0-48,21.528-48,48s21.528,48,48,48s48-21.528,48-48S162.472,304,136,304z M136,384c-17.648,0-32-14.352-32-32s14.352-32,32-32s32,14.352,32,32S153.648,384,136,384z"
        />
        <path
          fill="#0C3847"
          d="M48,368c-22.056,0-40,17.944-40,40s17.944,40,40,40s40-17.944,40-40S70.056,368,48,368z M48,432c-13.232,0-24-10.768-24-24s10.768-24,24-24s24,10.768,24,24S61.232,432,48,432z"
        />
        <path
          fill="#0C3847"
          d="M120,440c-17.648,0-32,14.352-32,32s14.352,32,32,32s32-14.352,32-32S137.648,440,120,440z M120,488c-8.824,0-16-7.176-16-16s7.176-16,16-16s16,7.176,16,16S128.824,488,120,488z"
        />

        <circle fill="#FFFFFF" cx="392" cy="376" r="16" />
      </g>
    </svg>
  );
};

export default GrokIcon;